import { ABILITY_NAMES, SKILLS, type Ability } from '@shared/rules/constants'
import { CLASSES_BY_COMPLEXITY, CLASS_BUILDS_BY_ID } from '@shared/rules/data/classes'
import { SPECIES_LIST, WITCHLIGHT_RECOMMENDED } from '@shared/rules/data/species'
import { BACKGROUNDS_LIST, WITCHLIGHT_BACKGROUNDS } from '@shared/rules/data/backgrounds'
import { FEATS_BY_ID } from '@shared/rules/data/feats'
import { EMPTY_FEAT_SELECTION, type CharacterDraft } from '@shared/rules/build'
import { Badge } from '@/components/ui'
import { ChoiceCard, Explainer, StepIntro, WitchlightBadge } from './shared'

export interface StepProps {
  draft: CharacterDraft
  update: (patch: Partial<CharacterDraft>) => void
}

const COMPLEXITY_TONE = { simple: 'moss', medium: 'gold', complex: 'blood' } as const
const COMPLEXITY_LABEL = { simple: 'Easy to play', medium: 'Some bookkeeping', complex: 'Lots of options' }

// ---------- Welcome ----------

export function StepWelcome() {
  return (
    <div>
      <StepIntro title="Let's make your character">
        This takes about ten minutes and you do not need to know any D&amp;D rules. Every question
        explains itself, nothing here is permanent, and you can go back and change anything before
        you finish.
      </StepIntro>

      <div className="grid gap-4 sm:grid-cols-3">
        {[
          { n: '1', title: 'Who you are', body: 'Pick a class, a species, and a background. These decide what you are good at.' },
          { n: '2', title: 'What you can do', body: 'Assign your ability scores, choose skills, and pick your gear and spells.' },
          { n: '3', title: 'Make it yours', body: 'Name your character, describe them, and download a filled-in sheet to keep.' },
        ].map((card) => (
          <div key={card.n} className="panel panel-lit p-4">
            <span className="grid size-7 place-items-center rounded-lg bg-gold-500 font-display text-sm font-bold text-on-accent">
              {card.n}
            </span>
            <h3 className="mt-3 font-display text-base">{card.title}</h3>
            <p className="mt-1.5 text-sm leading-relaxed text-ink-400">{card.body}</p>
          </div>
        ))}
      </div>

      <div className="mt-6 rounded-[--radius-card] border border-arcane-500/30 bg-arcane-900/25 p-4">
        <div className="flex items-center gap-2">
          <WitchlightBadge />
          <h3 className="font-display text-base">This campaign is The Wild Beyond the Witchlight</h3>
        </div>
        <p className="mt-2 text-sm leading-relaxed text-ink-300">
          It is a Feywild story that can be played start to finish without a single fight — charm,
          curiosity, and bargaining matter as much as combat. Two species and two backgrounds
          written for it are marked with a Witchlight tag as you go. You are not required to take
          them, but they fit the story especially well.
        </p>
      </div>
    </div>
  )
}

// ---------- Class ----------

export function StepClass({ draft, update }: StepProps) {
  return (
    <div>
      <StepIntro
        title="What do you want to do in the game?"
        note="You can change this freely until you finish — nothing is saved to the campaign yet."
      >
        Your class is the biggest decision here. It decides what your turn looks like in a fight,
        whether you cast spells, and how tough you are. Pick the one that sounds fun to play.
      </StepIntro>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {CLASSES_BY_COMPLEXITY.map((build) => (
          <ChoiceCard
            key={build.id}
            selected={draft.classId === build.id}
            onSelect={() => update({
              classId: build.id,
              // Everything downstream of class has to be re-picked.
              classSkills: [],
              classChoices: {},
              expertise: [],
              equipmentOptionId: '',
              cantrips: [],
              spells: [],
            })}
            title={build.name}
            tagline={build.tagline}
            badges={
              <>
                <Badge tone={COMPLEXITY_TONE[build.complexity]}>{COMPLEXITY_LABEL[build.complexity]}</Badge>
                <Badge>d{build.hitDie} hit die</Badge>
                {build.spellcasting && <Badge tone="arcane">Spellcaster</Badge>}
              </>
            }
          >
            <ul className="space-y-1">
              {build.playsLike.map((line) => (
                <li key={line} className="flex gap-2">
                  <span aria-hidden className="text-gold-500">•</span>
                  <span>{line}</span>
                </li>
              ))}
            </ul>
            <p className="mt-2.5 text-ink-500">
              Best with a high{' '}
              <span className="text-ink-300">
                {build.primaryAbility.map((a) => ABILITY_NAMES[a]).join(' or ')}
              </span>
            </p>
          </ChoiceCard>
        ))}
      </div>
    </div>
  )
}

// ---------- Species ----------

export function StepSpecies({ draft, update }: StepProps) {
  const ordered = [
    ...WITCHLIGHT_RECOMMENDED.map((id) => SPECIES_LIST.find((s) => s.id === id)!),
    ...SPECIES_LIST.filter((s) => !WITCHLIGHT_RECOMMENDED.includes(s.id)),
  ].filter(Boolean)

  const chosen = SPECIES_LIST.find((s) => s.id === draft.speciesId)

  return (
    <div>
      <StepIntro title="What kind of creature are you?">
        Your species gives you a handful of permanent traits — things like darkvision, extra hit
        points, or a flying speed. In these rules it does <em>not</em> change your ability scores,
        so pick whatever you find most interesting.
      </StepIntro>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {ordered.map((species) => (
          <ChoiceCard
            key={species.id}
            selected={draft.speciesId === species.id}
            onSelect={() => update({
              speciesId: species.id,
              speciesChoices: {},
              speciesSkills: [],
              speciesFeat: species.grantsOriginFeat ? { ...EMPTY_FEAT_SELECTION } : null,
            })}
            title={species.name}
            tagline={species.tagline}
            badges={
              <>
                {species.source === 'wbtw' && <WitchlightBadge />}
                <Badge>{species.size}</Badge>
                <Badge>Speed {species.speed}</Badge>
                {species.otherSpeeds?.fly && <Badge tone="arcane">Fly {species.otherSpeeds.fly}</Badge>}
                {species.darkvision > 0 && <Badge>Darkvision {species.darkvision}</Badge>}
              </>
            }
          >
            <p>{species.blurb}</p>
            {species.witchlightNote && (
              <p className="mt-2 text-arcane-400">{species.witchlightNote}</p>
            )}
          </ChoiceCard>
        ))}
      </div>

      {chosen?.choice && (
        <div className="mt-6 panel panel-lit p-4">
          <h2 className="font-display text-lg">{chosen.choice.label}</h2>
          <p className="mt-1 text-sm text-ink-400">{chosen.choice.help}</p>
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {chosen.choice.options.map((option) => (
              <ChoiceCard
                key={option.value}
                selected={draft.speciesChoices[chosen.choice!.id] === option.value}
                onSelect={() => update({
                  speciesChoices: { ...draft.speciesChoices, [chosen.choice!.id]: option.value },
                })}
                title={option.label}
                tagline={option.help}
              />
            ))}
          </div>
        </div>
      )}

      {chosen && (
        <div className="mt-6 panel p-4">
          <h2 className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-ink-400">
            What {chosen.name}s get
          </h2>
          <ul className="space-y-2.5">
            {chosen.traits.map((trait) => (
              <li key={trait.name} className="text-sm">
                <span className="font-medium text-ink-100">{trait.name}. </span>
                <span className="text-ink-400">{trait.text}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

// ---------- Background ----------

export function StepBackground({ draft, update }: StepProps) {
  const classBuild = draft.classId ? CLASS_BUILDS_BY_ID.get(draft.classId) : null
  const keyAbilities = (classBuild?.primaryAbility ?? []) as Ability[]

  const ordered = [
    ...BACKGROUNDS_LIST.filter((b) => WITCHLIGHT_BACKGROUNDS.includes(b.id)),
    ...BACKGROUNDS_LIST.filter((b) => !WITCHLIGHT_BACKGROUNDS.includes(b.id)),
  ]

  return (
    <div>
      <StepIntro title="What were you doing before this?">
        Your background is what your character did before becoming an adventurer. It is the single
        biggest source of your ability score bonuses, and it gives you two skills, a tool, and a
        feat — a special perk you keep forever.
      </StepIntro>

      {classBuild && (
        <p className="mb-4 max-w-2xl text-sm text-ink-400">
          A {classBuild.name} leans on{' '}
          <span className="text-moss-500">
            {classBuild.primaryAbility.map((a) => ABILITY_NAMES[a]).join(' or ')}
          </span>
          . Backgrounds that can raise it are marked — but any background works, and picking one you
          find interesting is a perfectly good reason.
        </p>
      )}

      <div className="mb-5">
        <Explainer term="ability score bonus">
          Everyone has six ability scores — Strength, Dexterity, Constitution, Intelligence, Wisdom,
          and Charisma. Higher is better. Your background lets you raise three of them, and you will
          choose exactly how on the next step.
        </Explainer>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {ordered.map((background) => {
          const feat = FEATS_BY_ID.get(background.featId)
          // Whether this background can raise what the chosen class actually needs.
          const boostsKeyAbility = keyAbilities.some((a) => background.abilities.includes(a))
          return (
            <ChoiceCard
              key={background.id}
              selected={draft.backgroundId === background.id}
              onSelect={() => update({
                backgroundId: background.id,
                backgroundIncreases: {},
                backgroundFeat: { ...EMPTY_FEAT_SELECTION, featId: background.featId },
                toolChoices: {},
              })}
              title={background.name}
              tagline={background.tagline}
              badges={
                <>
                  {background.source === 'wbtw' && <WitchlightBadge />}
                  {background.abilities.map((ability: Ability) => (
                    <Badge key={ability} tone={keyAbilities.includes(ability) ? 'moss' : 'gold'}>
                      {ABILITY_NAMES[ability].slice(0, 3)}
                    </Badge>
                  ))}
                  {classBuild && boostsKeyAbility && (
                    <Badge tone="moss">Boosts your {classBuild.name} ability</Badge>
                  )}
                </>
              }
            >
              <p>{background.blurb}</p>
              <dl className="mt-2.5 space-y-1">
                <div>
                  <dt className="inline text-ink-500">Skills: </dt>
                  <dd className="inline text-ink-300">
                    {background.skills.map((s) => SKILLS[s].name).join(', ')}
                  </dd>
                </div>
                <div>
                  <dt className="inline text-ink-500">Feat: </dt>
                  <dd className="inline text-ink-300">{feat?.name}</dd>
                  {feat && <span className="text-ink-500"> — {feat.tagline}</span>}
                </div>
                <div>
                  <dt className="inline text-ink-500">Tool: </dt>
                  <dd className="inline text-ink-300">{background.tool.label}</dd>
                </div>
              </dl>
              {background.witchlightNote && (
                <p className="mt-2 text-arcane-400">{background.witchlightNote}</p>
              )}
              {background.adapted && (
                <p className="mt-2 text-ink-500">
                  Written for the 2014 rules — its ability options and feat were chosen to fit the
                  2024 format. Your DM can adjust them.
                </p>
              )}
            </ChoiceCard>
          )
        })}
      </div>
    </div>
  )
}
